<template>
  <v-layout column>
    <v-layout row v-if="canManage">
      <v-flex>
        <v-btn :to="`/event/new`" color=success>
          <v-icon left>add</v-icon>
          Create Event
        </v-btn>
      </v-flex>
    </v-layout>

    <v-layout row class="mt-3">
      <v-flex>
        <h2 class="display-1 mb-2">Upcoming Events</h2>
        <v-data-table
          :items="upcomingEvents"
          :headers="headers"
          no-data-text="No upcoming events"
        >
          <template slot="items" slot-scope="{ item }">
            <tr>
              <td>
                <router-link
                  :to="{ name: 'eventDetail', params: { eventId: item.id } }"
                  target="_blank"
                >
                  {{ item.title }}
                </router-link>
              </td>
              <td>
                {{ item.start | moment("dddd, MMMM DD YYYY, H:mma") }}
              </td>
              <td>
                {{ item.location }}
              </td>
              <td>
                <span v-if="item.clockable">
                  clock
                </span>
              </td>
              <td v-if="canManage">
                <v-btn icon flat :to="{ name: 'eventEdit', params: { eventId: item.id } }" title="Edit event">
                  <v-icon>edit</v-icon>
                </v-btn>
                <v-btn icon flat color="error" @click="openDeleteDialog(item.id)" title="Delete event">
                  <v-icon>delete</v-icon>
                </v-btn>
              </td>
            </tr>
          </template>
        </v-data-table>
      </v-flex>
    </v-layout>

    <v-layout row class="mt-4">
      <v-flex>
        <h2 class="display-1 mb-2">Past Events</h2>
        <v-data-table
          :items="pastEvents"
          :headers="headers"
          no-data-text="No past events"
        >
          <template slot="items" slot-scope="{ item }">
            <tr>
              <td>
                <router-link
                  :to="{ name: 'eventDetail', params: { eventId: item.id } }"
                  target="_blank"
                >
                  {{ item.title }}
                </router-link>
              </td>
              <td>
                {{ item.start | moment("dddd, MMMM DD YYYY, H:mma") }}
              </td>
              <td>
                {{ item.location }}
              </td>
              <td>
                <span v-if="item.clockable">
                  clock
                </span>
              </td>
              <td v-if="canManage">
                <v-btn icon flat :to="{ name: 'eventEdit', params: { eventId: item.id } }" title="Edit event">
                  <v-icon>edit</v-icon>
                </v-btn>
                <v-btn icon flat color="error" @click="openDeleteDialog(item.id)" title="Delete event">
                  <v-icon>delete</v-icon>
                </v-btn>
              </td>
            </tr>
          </template>
        </v-data-table>
      </v-flex>
    </v-layout>

    <v-dialog v-model="deleteDialogOpen" max-width="480" persistent>
      <v-card>
        <v-card-title class="headline">Delete this event?</v-card-title>
        <v-card-text>
          <p>RSVPs will be removed.</p>
          <v-alert v-if="deleteDialogError" :value="true" type="error">
            {{ deleteDialogError }}
          </v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn flat @click="closeDeleteDialog" :disabled="deleting">Cancel</v-btn>
          <v-btn color="error" :loading="deleting" @click="handleConfirmDelete">Delete</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-layout>
</template>

<script>
import eventApi from '@/api/event'
import eventActionsMixin from '@/components/event/event-actions-mixin'
import moment from 'moment'
import { mapState } from 'vuex'

export default {
  mixins: [eventActionsMixin],
  data() {
    return {
      fetchingEvents: false,
      events: [],
    }
  },
  computed: {
    ...mapState("userAuth", ["user"]),
    canManage() {
      return !!(this.user && this.user.managerOrHigher)
    },
    headers() {
      const base = [
        { text: 'Title', value: 'title' },
        { text: 'Date', value: 'start' },
        { text: 'Location', value: 'location' },
        { text: 'Clock-in required?', value: 'clockable' },
      ]
      if (this.canManage) {
        base.push({ text: 'Actions', value: 'actions', sortable: false })
      }
      return base
    },
    upcomingEvents() {
      const startOfToday = moment().startOf('day')
      return this.events
        .filter((e) => moment(e.start).isSameOrAfter(startOfToday))
        .sort((a, b) => new Date(a.start) - new Date(b.start))
    },
    pastEvents() {
      const startOfToday = moment().startOf('day')
      return this.events
        .filter((e) => moment(e.start).isBefore(startOfToday))
        .sort((a, b) => new Date(b.start) - new Date(a.start))
    },
  },
  mounted() {
    this.getAllEvents()
  },
  methods: {
    getAllEvents() {
      this.fetchingEvents = true
      eventApi.getAll().then((r) => {
        this.events = r.data.events
      })
    },
    handleConfirmDelete() {
      this.confirmDelete().then((result) => {
        if (result.deleted) {
          this.$store.dispatch('global/showSnackbarSuccess', { message: 'Event cancelled' })
          this.events = this.events.filter((e) => e.id !== result.eventId)
        }
      })
    },
  },
}
</script>
